import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';

const wfApi = async (path, opts = {}) => {
  try {
    const res = await apiFetch(`/api/workflow${path}`, opts);
    if (!res.ok) return {};
    return res.json();
  } catch { return {}; }
};

export default function GroupTags() {
  const [tags, setTags] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [tagData, empRes] = await Promise.all([
      wfApi('/group-tags'),
      apiFetch('/api/employees?limit=500').then(r => r.ok ? r.json() : { employees: [] }),
    ]);
    setTags(tagData.tags || []);
    setEmployees(empRes.employees || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const empOptions = employees.filter(e => e.status === 'Active' || e.status === 'active')
    .map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name}${e.emp_code ? ` (${e.emp_code})` : ''}` }))
    .sort((a, b) => a.label.localeCompare(b.label));

  function openEdit(tag) {
    setEditRow(tag ? {
      id: tag.id,
      name: tag.name,
      description: tag.description || '',
      hr_employee_id: tag.hr_employee_id || '',
      admin_employee_id: tag.admin_employee_id || '',
      accounts_employee_id: tag.accounts_employee_id || '',
      buddy_employee_id: tag.buddy_employee_id || '',
      status: tag.status || 'Active',
      // Monitor opts this tag's employees into Workforce Live View tracking
      // (Teams presence + sign-in location). Off by default.
      monitor: !!tag.monitor,
    } : {
      name: '', description: '', hr_employee_id: '', admin_employee_id: '',
      accounts_employee_id: '', buddy_employee_id: '', status: 'Active',
      monitor: false,
    });
    setEditModal(true);
  }

  async function saveTag() {
    if (!editRow.name.trim()) return;
    await wfApi('/group-tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editRow),
    });
    setEditModal(false);
    fetchData();
  }

  async function deleteTag(id) {
    if (!confirm('Delete this group tag?')) return;
    await wfApi(`/group-tags/${id}`, { method: 'DELETE' });
    fetchData();
  }

  const filteredTags = tags.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.name.toLowerCase().includes(s) ||
           (t.description || '').toLowerCase().includes(s) ||
           (t.hr_employee_name || '').toLowerCase().includes(s) ||
           (t.admin_employee_name || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => openEdit(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Group Tag
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <input type="text" placeholder="Search tags..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', width: 300 }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>HR Employee</th>
              <th>Admin Employee</th>
              <th>Accounts Employee</th>
              <th>Buddy</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTags.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                {search ? 'No matching tags found' : 'No group tags defined yet. Click "New Group Tag" to create one.'}
              </td></tr>
            ) : filteredTags.map(t => (
              <tr key={t.id}>
                <td style={{ fontWeight: 500 }}>{t.name}</td>
                <td style={{ color: '#888' }}>{t.description || '—'}</td>
                <td>{t.hr_employee_name || <span style={{ color: '#555' }}>—</span>}</td>
                <td>{t.admin_employee_name || <span style={{ color: '#555' }}>—</span>}</td>
                <td>{t.accounts_employee_name || <span style={{ color: '#555' }}>—</span>}</td>
                <td>{t.buddy_employee_name || <span style={{ color: '#555' }}>—</span>}</td>
                <td>
                  <span className={`badge ${t.status === 'Active' ? 'badge-green' : 'badge-red'}`}>
                    {t.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-neon btn-sm" onClick={() => openEdit(t)} title="Edit">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button className="btn-neon btn-sm" onClick={() => deleteTag(t.id)} title="Delete" style={{ '--page-neon': '#ff4444' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Edit / Create Modal */}
      <Modal title={editRow?.id ? 'Edit Group Tag' : 'New Group Tag'} isOpen={editModal} onClose={() => setEditModal(false)} size="lg">
        {editRow && <>
          <FormField label="Tag Name" name="name" required
            value={editRow.name} placeholder="e.g. Engineering, Sales, Support"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <FormField label="Description" name="description"
            value={editRow.description} placeholder="Optional description"
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))} />
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 14, marginTop: 8 }}>
            <div style={{ fontSize: 13, color: '#00e0ff', marginBottom: 10, fontWeight: 500 }}>
              Assigned Contacts (for Workflow Routing)
            </div>
            <div className="form-row">
              <FormField label="HR Employee" name="hr_employee_id" type="employee-search"
                value={editRow.hr_employee_id}
                onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
                options={empOptions} placeholder="Search Employee..." />
              <FormField label="Admin Employee" name="admin_employee_id" type="employee-search"
                value={editRow.admin_employee_id}
                onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
                options={empOptions} placeholder="Search Employee..." />
            </div>
            <div className="form-row">
              <FormField label="Accounts Employee" name="accounts_employee_id" type="employee-search"
                value={editRow.accounts_employee_id}
                onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
                options={empOptions} placeholder="Search Employee..." />
              <FormField label="Buddy" name="buddy_employee_id" type="employee-search"
                value={editRow.buddy_employee_id}
                onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
                options={empOptions} placeholder="Search Employee..." />
            </div>
          </div>
          <FormField label="Status" name="status" type="select"
            value={editRow.status}
            onChange={(n, v) => setEditRow(prev => ({ ...prev, [n]: v }))}
            options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} />

          {/* Workforce Live View opt-in. When checked, this tag's employees
              show up on the Workforce Live View report with their Teams
              presence, sign-in city, leave/WFH status, and roster-compliance
              flag. Off by default — admin opts each tag in explicitly. */}
          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,224,255,0.04)' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!editRow.monitor}
                onChange={e => setEditRow(prev => ({ ...prev, monitor: e.target.checked }))}
                style={{ marginTop: 3 }} />
              <span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#E5E7EB' }}>Monitor on Workforce Live View</span>
                <span style={{ display: 'block', marginTop: 3, fontSize: 11.5, color: '#94A3B8', lineHeight: 1.5 }}>
                  Pull Teams presence and sign-in location for employees in this tag, and surface them
                  on the HCM → Workforce Live report. Status updates every ~60 seconds via Microsoft Graph.
                </span>
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button className="btn btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveTag}>Save</button>
          </div>
        </>}
        </Modal>
    </div>
  );
}
